import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ChevronRight, Loader2, AlertCircle, CheckCircle2, XCircle,
  BookOpen, HelpCircle, Zap, ExternalLink,
  Globe2, Shield, TrendingUp, Cpu, Leaf, Trophy,
  Star, Landmark, Users, Newspaper,
} from "lucide-react";
import caApi from "../api/ca";
import SEOHead from "../components/SEOHead";

const CAT_ICONS = {
  National: Landmark, International: Globe2, Defence: Shield,
  Economy: TrendingUp, "Science & Technology": Cpu, Environment: Leaf,
  "Awards & Honours": Trophy, Sports: Star, "Government Schemes": BookOpen,
  Appointments: Users, Miscellaneous: Newspaper,
};
const CAT_COLORS = {
  National: "#92400E", International: "#1E3A8A", Defence: "#7C2D12",
  Economy: "#064E3B", "Science & Technology": "#312E81", Environment: "#14532D",
  "Awards & Honours": "#78350F", Sports: "#831843", "Government Schemes": "#1E40AF",
  Appointments: "#4C1D95", Miscellaneous: "#374151",
};
const CAT_BG = {
  National: "#FEF3C7", International: "#EFF6FF", Defence: "#FFF7ED",
  Economy: "#ECFDF5", "Science & Technology": "#EEF2FF", Environment: "#F0FDF4",
  "Awards & Honours": "#FFFBEB", Sports: "#FDF2F8", "Government Schemes": "#EFF6FF",
  Appointments: "#F5F3FF", Miscellaneous: "#F9FAFB",
};
const IMP_COLOR = { high: "#DC2626", medium: "#D97706", low: "#6B7280" };
const IMP_BG    = { high: "#FEE2E2", medium: "#FEF3C7", low: "#F3F4F6" };

function fmt(dateStr) {
  if (!dateStr) return "";
  return new Date(dateStr + "T00:00:00").toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

// ── MCQ quiz block ─────────────────────────────────────────────────────────────
function MCQBlock({ mcqs }) {
  const [answers, setAnswers] = useState({});
  if (!mcqs?.length) return null;

  return (
    <div className="mt-6">
      <div className="flex items-center gap-2 mb-4">
        <HelpCircle size={16} className="text-indigo-600" />
        <h3 className="font-sora font-extrabold text-sm text-gray-900">Practice MCQs</h3>
        <span className="text-xs text-indigo-500 font-semibold">{mcqs.length} questions</span>
      </div>
      <div className="flex flex-col gap-4">
        {mcqs.map((mcq, qi) => {
          const chosen = answers[qi];
          const answered = chosen !== undefined;
          return (
            <div key={qi} className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
              <p className="text-sm font-semibold text-gray-800 mb-3 leading-snug">
                <span className="text-indigo-500 font-bold mr-1.5">Q{qi + 1}.</span>
                {mcq.q}
              </p>
              <div className="flex flex-col gap-1.5">
                {mcq.opts.map((opt, oi) => {
                  const isCorrect = oi === mcq.ans;
                  const isChosen  = chosen === oi;
                  let bg = "#F9FAFB", border = "#E5E7EB", textColor = "#374151";
                  if (answered) {
                    if (isCorrect)       { bg = "#F0FDF4"; border = "#22C55E"; textColor = "#15803D"; }
                    else if (isChosen)   { bg = "#FEF2F2"; border = "#EF4444"; textColor = "#DC2626"; }
                  }
                  return (
                    <button
                      key={oi}
                      disabled={answered}
                      onClick={() => setAnswers(a => ({ ...a, [qi]: oi }))}
                      className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl border text-xs font-medium text-left transition-all"
                      style={{ background: bg, borderColor: border, color: textColor }}
                    >
                      <span className="w-5 h-5 rounded-full border flex-shrink-0 flex items-center justify-center text-[10px] font-bold"
                        style={{ borderColor: border }}>
                        {String.fromCharCode(65 + oi)}
                      </span>
                      <span className="flex-1">{opt}</span>
                      {answered && isCorrect && <CheckCircle2 size={14} className="flex-shrink-0 text-green-500" />}
                      {answered && isChosen && !isCorrect && <XCircle size={14} className="flex-shrink-0 text-red-500" />}
                    </button>
                  );
                })}
              </div>
              {answered && mcq.exp && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  className="mt-3 px-3 py-2 rounded-xl bg-blue-50 border border-blue-100"
                >
                  <p className="text-xs text-blue-700 leading-relaxed">
                    <span className="font-bold">Explanation: </span>{mcq.exp}
                  </p>
                </motion.div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main detail page ───────────────────────────────────────────────────────────
export default function CurrentAffairDetail() {
  const { slug }    = useParams();
  const navigate    = useNavigate();
  const [item, setItem]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);

  useEffect(() => {
    setLoading(true);
    caApi.getBySlug(slug)
      .then(setItem)
      .catch(() => setError("Article not found."))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return (
    <div className="min-h-screen bg-[#F5F6FA] flex items-center justify-center">
      <Loader2 size={28} className="animate-spin text-gray-300" />
    </div>
  );

  if (error || !item) return (
    <div className="min-h-screen bg-[#F5F6FA] flex flex-col items-center justify-center gap-3">
      <AlertCircle size={32} className="text-red-400" />
      <p className="text-sm text-red-500">{error || "Not found"}</p>
      <button onClick={() => navigate("/ca")} className="text-sm text-green-700 font-semibold underline">
        Back to Current Affairs
      </button>
    </div>
  );

  const CatIcon  = CAT_ICONS[item.category]  || Newspaper;
  const catColor = CAT_COLORS[item.category] || "#374151";
  const catBg    = CAT_BG[item.category]    || "#F9FAFB";

  return (
    <div className="min-h-screen bg-[#F5F6FA] font-body">
      {item && (
        <SEOHead
          title={item.title}
          description={item.summary || item.title}
          keywords={`current affairs ${item.category}, ${item.title}, NDA current affairs, CDS current affairs`}
          url={`/ca/${slug}`}
          jsonLd={{ "@context": "https://schema.org", "@type": "Article", "headline": item.title, "description": item.summary || item.title, "datePublished": item.dateStr, "publisher": { "@type": "Organization", "name": "EduPulseAI" } }}
        />
      )}
      {/* Sticky header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-2 text-xs text-gray-400">
          <button onClick={() => navigate("/ca")} className="hover:text-gray-700 font-medium transition-colors">
            Current Affairs
          </button>
          <ChevronRight size={12} />
          <span className="text-gray-700 font-semibold truncate">{item.title}</span>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">

        {/* ── Article card ─────────────────────────────────────────────────── */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-5">

          {/* Coloured top bar */}
          <div className="h-1.5" style={{ background: catColor }} />

          <div className="p-6">
            {/* Category + importance + date */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg" style={{ background: catBg }}>
                <CatIcon size={12} style={{ color: catColor }} />
                <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: catColor }}>
                  {item.category}
                </span>
              </div>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                style={{ background: IMP_BG[item.importance], color: IMP_COLOR[item.importance] }}>
                {item.importance} priority
              </span>
              <span className="ml-auto text-[11px] text-gray-400 font-medium">{fmt(item.dateStr)}</span>
            </div>

            {/* Title */}
            <h1 className="font-sora font-black text-xl text-gray-900 leading-snug mb-3">{item.title}</h1>

            {/* Summary */}
            {item.summary && (
              <p className="text-sm text-gray-600 leading-relaxed mb-4 border-l-4 pl-3" style={{ borderColor: catColor }}>
                {item.summary}
              </p>
            )}

            {/* Exam tags */}
            {item.examTags?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-4">
                {item.examTags.map(tag => (
                  <span key={tag} className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-gray-100 text-gray-600">
                    {tag}
                  </span>
                ))}
              </div>
            )}

          </div>
        </div>

        {/* ── Key Facts ─────────────────────────────────────────────────────── */}
        {item.facts?.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 mb-5">
            <div className="flex items-center gap-2 mb-4">
              <Zap size={15} className="text-amber-500" />
              <h3 className="font-sora font-extrabold text-sm text-gray-900">Key Facts</h3>
            </div>
            <div className="flex flex-col gap-2.5">
              {item.facts.map((f, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.04 }}
                  className="flex items-start gap-3"
                >
                  <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ background: f.examTip ? "#FEF3C7" : "#F3F4F6" }}>
                    <span className="text-[9px] font-black" style={{ color: f.examTip ? "#B45309" : "#6B7280" }}>
                      {i + 1}
                    </span>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-700 leading-relaxed">{f.fact}</p>
                    {f.examTip && (
                      <span className="text-[9px] font-bold text-amber-600 uppercase tracking-wide">★ Exam Tip</span>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* ── MCQs ──────────────────────────────────────────────────────────── */}
        {item.mcqs?.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <MCQBlock mcqs={item.mcqs} />
          </div>
        )}
      </div>
    </div>
  );
}
